/** @jsx jsx */
import { useLocation } from "@reach/router"
import { graphql, useStaticQuery } from "gatsby"
import PropTypes from "prop-types"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { jsx } from "theme-ui"

import useLocaleContext from "@hooks/useLocaleContext"

/** Only used with Media */
export const fragment = graphql`
  fragment MediaForm on ContentfulForm {
    backgroundcolor
    formWidget
  }
`

export default function BroccForm({ hero, loan, widget, messageHandler }) {
  const iframeRef = useRef(null)
  const { search } = useLocation()
  const locale = useLocaleContext()

  const { brocc } = useStaticQuery(
    graphql`
      {
        brocc {
          origin
        }
      }
    `
  )

  const getFormTitle = () => {
    switch (widget) {
      case "loan-application":
        return "Brocc låneformulär"
      case "fixed-deposit-application":
        return "Brocc fast ränta formulär"
      case "flex-deposit-application":
        return "Brocc flexibel ränta formulär"
      default:
        return "Brocc formulär"
    }
  }

  const config = useMemo(() => {
    // The widget now lives on the `widget.` subdomain with only two forms:
    // `/loan/` for loan applications and `/deposit/` for deposit applications.
    const widgetOrigin = brocc.origin.replace(/^(https?:\/\/)/, "$1widget.")
    const form = widget === "loan-application" ? "loan" : "deposit"
    const query = search ? `${search}&lang=${locale}` : `?lang=${locale}`

    return {
      origin: widgetOrigin,
      src: `${widgetOrigin}/${form}/${query}`,
    }
  }, [brocc.origin, search, widget, locale])

  const handleMessage = useCallback(
    evt => {
      const { data, origin } = evt

      if (origin !== config.origin) {
        return
      }

      if (!data) {
        return
      }

      if (messageHandler) {
        messageHandler(data)
      }

      if (data.height) {
        iframeRef.current.style.height = data.height + "px"
      }
    },
    [config, iframeRef]
  )

  const handleResize = useCallback(() => {
    iframeRef?.current?.contentWindow?.postMessage("height", config.origin)
  }, [config, iframeRef])

  const handleResponsiveness = useCallback(() => {
    const isBrowser = typeof window !== "undefined"
    if (isBrowser) {
      const message = window?.innerWidth < 768 ? "font-sm" : "font-regular"
      iframeRef?.current?.contentWindow?.postMessage(message, config.origin)
    }
    handleResize()
  }, [config.origin, handleResize])

  useEffect(() => {
    const iframe = iframeRef?.current
    if (iframe) {
      window.addEventListener("load", handleResponsiveness)
      window.addEventListener("message", handleMessage)
      window.addEventListener("resize", handleResponsiveness)
    }

    return () => {
      if (iframe) {
        window.removeEventListener("load", handleResponsiveness)
        window.removeEventListener("message", handleMessage)
        window.removeEventListener("resize", handleResponsiveness)
      }
    }
  }, [handleMessage, handleResponsiveness, iframeRef])

  useLayoutEffect(() => {
    iframeRef.current.onload = () => {
      handleResize()
    }
  }, [handleResize, iframeRef])

  useEffect(() => {
    const intervalID = setInterval(() => {
      handleResize()
    }, 50)

    const timeoutID = setTimeout(() => {
      if (intervalID) {
        clearInterval(intervalID)
      }
    }, 5000)

    return () => {
      if (intervalID) {
        clearInterval(intervalID)
      }
      if (timeoutID) {
        clearTimeout(timeoutID)
      }
    }
  }, [handleResize])

  return (
    <div
      sx={{
        alignSelf: "start",
        backgroundColor: loan ? "#F4F6EE" : "#FFFFFF",
        // Nested radius rule: outer = inner + padding. Loan frames the inner
        // 12px cards with 12px padding, so the outer radius is 24px.
        borderRadius: loan ? "24px" : "12px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: loan ? "12px" : "0px",
        position: "relative",
        width: "100%",

        "::before": {
          content: '""',
          display: "none",
          position: "absolute",

          "@media screen and (min-width: 992px)": {
            backgroundImage: loan
              ? `url(${
                  hero
                    ? "/imgs/foursparklestopright.svg"
                    : "/imgs/foursparklestopleft.svg"
                })`
              : null,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            display: loan ? "inline-block" : "none",
            left: hero ? "unset" : "-60px",
            height: hero ? 70 : 90,
            right: hero ? "-5px" : "unset",
            top: hero ? "-60px" : "-60px",
            transform: hero ? "rotate(-40deg)" : "unset",
            width: hero ? 70 : 80,
          },
          "@media screen and (min-width: 1200px)": {
            height: 90,
            left: hero ? "unset" : "-60px",
            right: hero ? "-50px" : "unset",
            top: hero ? "-70px" : "-60px",
            transform: "unset",
            width: 80,
          },
        },
      }}
    >
      <iframe
        ref={iframeRef}
        src={config.src}
        sx={{
          border: "none",
          display: "block",
          width: "100%",
          // On the loan form the green container frames the widget, so the
          // iframe sits on its own white card with rounded corners.
          ...(loan && {
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
          }),
        }}
        title={getFormTitle()}
      />

      {loan && (
        <div
          sx={{
            display: "grid",
            gridTemplateColumns: "56px 1fr",
            gap: "16px",
            padding: "16px",
            margin: "20px 0 0",
            background: "#FFFFFF",
            borderRadius: "12px",
          }}
        >
          <div>
            <img src="/imgs/symbol.svg" alt="" />
          </div>
          <div>
            <p
              sx={{
                margin: 0,
                fontSize: "13px",
                lineHeight: "140%",
              }}
            >
              <strong>Att låna kostar pengar!</strong>
              <br />
              Om du inte kan betala tillbaka skulden i tid riskerar du en
              betalningsanmärkning. Det kan leda till svårigheter att få hyra
              bostad, teckna abonnemang och få nya lån. För stöd, vänd dig till
              budget- och skuldrådgivningen i din kommun. Kontaktuppgifter finns
              på{" "}
              <a
                rel="nofollow noopener"
                href="https://www.konsumentverket.se/"
                target="_blank"
              >
                konsumentverket.se.
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

BroccForm.defaultProps = {
  hero: false,
  loan: false,
}

BroccForm.propTypes = {
  hero: PropTypes.bool,
  loan: PropTypes.bool,
  widget: PropTypes.string.isRequired,
  messageHandler: PropTypes.func,
}
